import { useEffect, useState } from "react"
import { useForm, FieldValues } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { useToast } from "@/contexts/ToastContext"
import { motion } from "framer-motion"

import { useAuth } from "@/modules/Auth/context/AuthContext"
import { updateUser } from "@/modules/Users/service/UsersService"
import { parseConsumoToForm, parseFormToEdicao } from "@/modules/Users/utils/dataTransformers"
import { getPessoas } from "@/services/companyService"
import { MemberFormFields } from "@/modules/Users/components/MemberFormFields"

import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { EDICAO_PessoaDataType } from "@/types/membros"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon } from "@/components/Atoms/Icon"

export function EditMemberPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const memberId = Number(id)
  const toast = useToast()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const form = useForm({
    mode: "onChange", // Importante para validar o estado a cada mudança
  })

  // 1. Monitora os valores do formulário para validar o botão de submit
  const formValues = form.watch()

  // 2. Lógica de validação: verifica se os campos obrigatórios estão vazios
  const isFormIncomplete =
    !formValues.email?.toString().trim() ||
    !formValues.cpf?.toString().trim() ||
    !formValues.nome_completo?.toString().trim() ||
    !formValues.cargo?.toString().trim() ||
    !formValues.perfil?.toString().trim()

  useEffect(() => {
    if (memberId && user) {
      setIsLoading(true)
      getPessoas(user.empId, { pes_id: String(memberId) })
        .then(({ data }) => {
          if (data.data && data.data.length > 0) {
            const parsedData: FieldValues = parseConsumoToForm(data.data[0], user.pesId)
            form.reset(parsedData)
            form.trigger()
          } else {
            toast.danger("Usuário não encontrado.")
            navigate("/users")
          }
        })
        .catch((error) => {
          console.error(error)
          toast.danger("Falha ao carregar os dados do usuário.")
          navigate("/users")
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [memberId, user, form, navigate])

  const onSubmit = async (data: FieldValues) => {
    if (!user) {
      toast.danger("Usuário não autenticado.")
      return
    }

    setIsSubmitting(true)
    try {
      const dataForParsing: any = {
        ...data,
        pes_id: memberId,
        originador_id: user.pesId,
      }

      const apiPayload: EDICAO_PessoaDataType = parseFormToEdicao(dataForParsing)

      await updateUser(apiPayload)

      toast.success("Usuário atualizado com sucesso!")
      navigate("/users")
    } catch (error) {
      console.error(error)
      toast.danger("Ocorreu um erro ao atualizar o usuário.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.4 } },
  }

  return (
    <section className="w-full animate-fade-in py-10">
      <header className="mx-6 mx-auto flex w-[calc(100vw-20%)] max-w-[800px] flex-shrink-0 gap-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-orange-500"
          type="button"
        >
          <Icon name="arrow-left" />
        </button>
        <div className="flex flex-col gap-1">
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={itemVariants}
            className="text-2xl font-black md:text-4xl"
          >
            Editar usuário
          </motion.h1>
        </div>
      </header>

      <div className="mx-auto w-[calc(100vw-20%)] max-w-[800px]">
        {isLoading ? (
          <div className="space-y-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
            <Skeleton className="h-32 w-full" />
            <div className="flex justify-end space-x-4">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-8"
            >
              <MemberFormFields
                control={form.control as any}
                mode="edit"
              />

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate("/users")}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={isFormIncomplete ? 0 : undefined}>
                      <Button
                        type="submit"
                        className="bg-orange-500 text-white hover:bg-orange-500 hover:text-white disabled:bg-zinc-700 disabled:text-zinc-400"
                        disabled={isSubmitting || isFormIncomplete}
                      >
                        {isSubmitting ? "Salvando..." : "Salvar"}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isFormIncomplete && !isSubmitting && (
                    <TooltipContent>
                      Preencha todos os campos obrigatórios para salvar as alterações.
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            </form>
          </Form>
        )}
      </div>
    </section>
  )
}
